import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JointViewerControls from "./jointviewercontrols";
import Joint from "../models/joint";
import BabylonCanvas from "./babylon-canvas";
import { Scene } from "@babylonjs/core";
import JointRenderSpecs from "../models/joint-render-specs";
import JointRenderer from "../models/joint-renderer";
import MachineSettings from "../models/machine-settings";
import JointSceneSettings from "../models/joint-scene-settings";
import JointScene from "../models/joint-scene";

interface JointProps {
    joint: Joint | null;
    machineSettings: MachineSettings;
}

export default function JointViewer(props: JointProps) {
    const { joint, machineSettings } = props;
    const sceneSettings = useRef(new JointSceneSettings());
    const [jointScene, setJointScene] = useState<JointScene | undefined>();
    const jointRenderSpecs = useMemo(() => joint && new JointRenderSpecs(joint, machineSettings), [joint, machineSettings]);
    const jointRenderer = useRef<JointRenderer | null>(null);

    useEffect(() => {
        if (!jointScene || !jointRenderSpecs) return;

        const renderer = new JointRenderer(jointRenderSpecs);
        renderer.renderToScene(jointScene, sceneSettings.current.folded);
        renderer.applySceneSettings(sceneSettings.current);

        jointRenderer.current = renderer;

        return () => jointScene.clear();
    }, [jointScene, jointRenderSpecs]);

    const applySceneSettings = useCallback((newSettings: JointSceneSettings, prevSettings: JointSceneSettings) => {
        sceneSettings.current = newSettings;

        jointScene?.applySceneSettings(newSettings, prevSettings);
        jointRenderer.current?.applySceneSettings(newSettings);
    }, [jointScene]);

    const initScene = useCallback((scene: Scene) => {
        setJointScene(new JointScene(scene));
    }, []);

    return (
        <div className="jointviewer">
            <JointViewerControls
                joint={joint}
                initialSettings={sceneSettings.current}
                onSettingsChange={applySceneSettings}
            />
            <BabylonCanvas
                initScene={initScene}
            />
        </div>
    );
}
